import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, Plus, ShieldAlert, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Project, WorkItemType } from '../../shared/types';
import { api } from '../lib/api';

const kinds: Array<{ value: WorkItemType; label: string; icon: typeof Plus }> = [
  { value: 'task', label: 'Task', icon: Plus },
  { value: 'roadblock', label: 'Roadblock', icon: ShieldAlert },
  { value: 'improvement', label: 'Improvement', icon: Sparkles },
  { value: 'feature', label: 'Future feature', icon: Lightbulb },
];

export function QuickCapture({ projects, open, onClose }: { projects: Project[]; open: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const draft = (() => { try { return JSON.parse(localStorage.getItem('foundry:capture-draft') || '{}'); } catch { return {}; } })();
  const [projectId, setProjectId] = useState<string>(draft.projectId || projects[0]?.id || '');
  const [title, setTitle] = useState<string>(draft.title || '');
  const [detail, setDetail] = useState<string>(draft.detail || '');
  const [type, setType] = useState<WorkItemType>(draft.type || 'task');
  useEffect(() => { localStorage.setItem('foundry:capture-draft', JSON.stringify({ projectId, title, detail, type })); }, [projectId, title, detail, type]);
  useEffect(() => { if (!projectId && projects[0]) setProjectId(projects[0].id); }, [projectId, projects]);
  const mutation = useMutation({
    mutationFn: () => api.createWorkItem(projectId || projects[0]?.id, { title, detail, type, state: type === 'roadblock' ? 'blocked' : 'backlog', priority: type === 'roadblock' ? 'high' : 'medium' }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['projects'] });
      localStorage.removeItem('foundry:capture-draft'); setTitle(''); setDetail(''); setType('task'); onClose();
    },
  });
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="capture-title">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close quick capture"><X /></button>
        <span className="mono eyebrow">INBOX / QUICK CAPTURE</span>
        <h2 id="capture-title">Catch it before it disappears.</h2>
        <div className="kind-picker">
          {kinds.map((kind) => <button key={kind.value} className={type === kind.value ? 'active' : ''} onClick={() => setType(kind.value)}><kind.icon size={16} /> {kind.label}</button>)}
        </div>
        <label>Project<select value={projectId || projects[0]?.id} onChange={(event) => setProjectId(event.target.value)}>{projects.map((project) => <option value={project.id} key={project.id}>{project.name}</option>)}</select></label>
        <label>Title<input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What needs attention?" /></label>
        <label>Context<textarea value={detail} onChange={(event) => setDetail(event.target.value)} placeholder="Add just enough context for future you." rows={4} /></label>
        {mutation.error && <p className="form-error">{mutation.error.message}</p>}
        <button className="primary-button" disabled={title.trim().length < 2 || mutation.isPending} onClick={() => mutation.mutate()}>{mutation.isPending ? 'Capturing…' : 'Add to the Foundry'}</button>
      </section>
    </div>
  );
}
